// Thin fetch wrapper for the oneselfpath API (same shape as intellinurse/src/lib/api.ts).
// Every request carries the signed-in user's Firebase ID token; the backend derives
// the patient from it, so the client never sends a patient id.
import { IS_MOCK_AUTH, getFirebaseAuth } from "./firebase";

export class ApiError extends Error {
  status: number;
  code: string;
  body: unknown;

  constructor(status: number, message: string, body?: unknown, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.code = code ?? `HTTP_${status}`;
  }

  get isAuthError() {
    return this.status === 401;
  }
  get isPermissionError() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isServerError() {
    return this.status >= 500;
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

async function getAuthHeaders(): Promise<Record<string, string>> {
  if (IS_MOCK_AUTH) return {};
  const user = getFirebaseAuth().currentUser;
  if (!user) return {};
  return { Authorization: `Bearer ${await user.getIdToken()}` };
}

export type Query = Record<string, string | number | boolean | undefined | null>;

export function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const qs = Object.entries(query)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");
  return qs ? `${path}${path.includes("?") ? "&" : "?"}${qs}` : path;
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const authHeaders = await getAuthHeaders();
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
        ...(options.headers ?? {}),
      },
    });
  } catch (err) {
    throw new ApiError(0, err instanceof Error ? err.message : "Network error", undefined, "NETWORK");
  }

  if (!response.ok) {
    let body: unknown = undefined;
    let message = response.statusText || `HTTP ${response.status}`;
    try {
      body = await response.json();
      const b = body as { detail?: string; message?: string };
      message = b.detail ?? b.message ?? message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(response.status, message, body);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => apiFetch<T>(withQuery(path, query)),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: "PATCH", body: JSON.stringify(body ?? {}) }),
  delete: <T = void>(path: string) => apiFetch<T>(path, { method: "DELETE" }),
};
